import { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import EditModal from 'components/UI/EditModal';
import Button from 'components/UI/Button';
import { Content, ButtonGrid } from './styled';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import InputField from 'components/UI/InputField';
import { useAppDispatch } from 'hooks/redux';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import { SoftwareReleaseAttribute } from 'models';
import { format } from 'date-fns';
import { releaseSoftwareToProduct } from 'store/softwareState/softwareState';
import { useParams } from 'react-router';

type Props = {
  softwareName: string;
  open: boolean;
  onClose: () => void;
};

const ReleaseModal: FC<Props> = ({ softwareName, open, onClose }) => {
  const dispatch = useAppDispatch();
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm();
  const { id } = useParams();

  const setDate = useCallback(
    (name: SoftwareReleaseAttribute) => (date: Date) => {
      setValue(name, format(date, 'yyyy-MM-dd'), {
        shouldDirty: true,
      });
    },
    [setValue]
  );

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values: FieldValues) => {
      const pnc = values.pnc;
      const releaseDate = new Date(values.releaseDate);
      if (pnc && releaseDate && id) {
        dispatch(releaseSoftwareToProduct(id, pnc, releaseDate));
        onClose();
      }
    },
    [dispatch, id, onClose]
  );

  return (
    <EditModal title="software_release.title" open={open} close={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Content>
          <Heading type={HeadingType.h4}>
            <FormattedMessage id="software_release.sub_title_part_1" />
            {softwareName}
            <FormattedMessage id="software_release.sub_title_part_2" />
          </Heading>
          <InputField
            description="software_release.pnc"
            error={errors.pnc}
            register={register(SoftwareReleaseAttribute.PNC, {
              required: { value: true, message: 'input.required' },
            })}
          />
          <InputField
            type="date"
            description="software_release.release_date"
            setDate={setDate(SoftwareReleaseAttribute.RELEASE_DATE)}
            register={register(SoftwareReleaseAttribute.RELEASE_DATE, {
              required: { value: true, message: 'input.required' },
            })}
            error={errors.releaseDate}
          />
        </Content>
        <ButtonGrid>
          <Button
            onClick={onClose}
            backgroundColor="transparent"
            color="primary"
          >
            <FormattedMessage id="modal.btn_close" />
          </Button>
          <Button submit>
            <FormattedMessage id="software_release.btn_save" />
          </Button>
        </ButtonGrid>
      </form>
    </EditModal>
  );
};

export default ReleaseModal;
